// lib/takeoff/rfiSuggest.js — RFI questions found on the takeoff (no AI): wall tags with no assembly detail,
// assemblies missing what we need to price them, untagged walls, tags that don't match the wall drawn, doors to
// confirm, heights not given. Each comes with a spot on the drawing so the RFI can be pinned there.
//
//   drawingSuggestions(payload) → [{ key, question, why, severity, source: { kind:"drawing", label, page, tag,
//                                    conditionId }, pin: { page, lat, lng } | null }]

const sheet = (payload, page) => {
  const n = payload.pageNames?.[page] ?? payload.pageNames?.[String(page)];
  const label = typeof n === "string" ? n : n?.number || n?.name || n?.title;
  return label ? `sheet ${String(label).split(/\s+[—–-]\s+/)[0]}` : `page ${page}`;
};
const midOf = (s) => {
  const ll = s?.latlngs;
  if (!ll) return null;
  const flat = Array.isArray(ll[0]?.[0]) ? ll[0] : ll; // polygon rings → first ring
  const p = Array.isArray(flat[0]) ? flat[Math.floor(flat.length / 2)] || flat[0] : flat;
  const lat = Array.isArray(p) ? p[0] : p?.lat;
  const lng = Array.isArray(p) ? p[1] : p?.lng;
  return Number.isFinite(lat) && Number.isFinite(lng) ? { page: s.page, lat, lng } : null;
};
const normTag = (t) => String(t || "").trim().toUpperCase().replace(/\s+/g, "");
const buildUp = (a) =>
  [a.name, a.description, ...(a.layers || []).map((l) => (typeof l === "string" ? l : [l?.name, l?.material, l?.description].filter(Boolean).join(" ")))]
    .filter(Boolean)
    .join(" ");

export function drawingSuggestions(payload) {
  if (!payload) return [];
  const out = [];
  const shapes = payload.shapes || [];
  const conds = payload.conditions || [];
  const asms = (payload.assemblies || []).filter((a) => a && (a.tag || a.name));
  const excluded = new Set((payload.excludedPages || []).map(Number));
  const live = shapes.filter((s) => !excluded.has(Number(s.page)));
  const walls = live.filter((s) => s.kind === "polyline" && (s.meta?.role === "wall" || conds.find((c) => c.id === s.conditionId)?.type === "linear"));
  const byTag = new Map(asms.map((a) => [normTag(a.tag), a]));

  // 1. wall tags on the plans with no assembly detail
  const tagged = new Map();
  for (const s of walls) {
    const t = normTag(s.meta?.wallTag);
    if (!t) continue;
    if (!tagged.has(t)) tagged.set(t, []);
    tagged.get(t).push(s);
  }
  for (const t of payload.wallTagSettings?.enabled || []) if (!tagged.has(normTag(t))) tagged.set(normTag(t), []);
  for (const [t, list] of tagged) {
    if (byTag.has(t)) continue;
    const pages = [...new Set(list.map((s) => s.page))].sort((a, b) => a - b);
    out.push({
      key: `tag-missing:${t}`,
      severity: "high",
      question: `Wall type ${t} is tagged on the plans${pages.length ? ` (${pages.map((p) => sheet(payload, p)).join(", ")}${list.length ? `, ${list.length} wall run${list.length === 1 ? "" : "s"}` : ""})` : ""}, but there is no ${t} detail in the partition / wall type schedule. Please provide the ${t} assembly: board type and layers each side, stud size, gauge and spacing, insulation, height (to deck or above ceiling) and any fire / STC rating.`,
      why: "Can't price a wall type without its build-up",
      source: { kind: "drawing", label: `${pages.length ? `${sheet(payload, pages[0])} · ` : ""}${t}`, page: pages[0] ?? null, tag: t },
      pin: midOf(list[0]),
    });
  }

  // 2. assemblies missing what the price needs
  for (const a of asms) {
    const txt = buildUp(a);
    const isCeiling = /ceil|soffit|bulkhead/i.test(`${a.kind} ${a.name}`);
    const missing = [];
    if (!/gyp|gwb|drywall|board|type\s*x|type\s*c|glass\s*mat|cement|plaster|5\/8|1\/2|16\s*mm|13\s*mm|12\.7|15\.9|act|tile|panel/i.test(txt)) missing.push(isCeiling ? "the ceiling finish / board" : "the board type and layers");
    if (!isCeiling && !/stud|framing|furring|hat|channel|z-?bar|\b\d{2,3}\s*mm\b|3-?5\/8|2-?1\/2|1-?5\/8|\b6"|\b4"|\b362|\b250|\b600|\b400|cmu|block|masonry|concrete/i.test(txt)) missing.push("the stud size");
    if (!isCeiling && /stud|framing/i.test(txt) && !/\d+\s*(?:ga|gauge|mil)\b|\d\.\d+\s*mm|0\.\d+\s*mm/i.test(txt)) missing.push("the stud gauge");
    if (!isCeiling && /stud|framing/i.test(txt) && !/o\.?\s*c|@\s*\d|spacing|centres|centers|\d+\s*(?:"|in|mm)\s*o/i.test(txt)) missing.push("the stud spacing");
    const cond = conds.find((c) => c.id === a.conditionId || c.assemblyId === a.id);
    const h = Number(a.height) || Number(cond?.height);
    if (!h && !/deck|u\/s|underside|structure|above (?:the )?ceiling|full height|\d+'\s*-?\s*\d*"?|\d{4}\s*mm|height/i.test(txt))
      missing.push(isCeiling ? "the ceiling height" : "the wall height (to the deck or to above the ceiling)");
    if (a.fireRating && !a.ul && !/ul[c]?\s*[a-z]?\s*-?\s*\d|design\s*(?:no|#)/i.test(txt)) missing.push(`the ULC / UL design number for its ${a.fireRating} rating`);
    if (!missing.length) continue;
    const s0 = shapes.find((s) => s.conditionId === (cond?.id || a.conditionId)) || (byTag.get(normTag(a.tag)) && walls.find((s) => normTag(s.meta?.wallTag) === normTag(a.tag)));
    const name = a.tag ? `Wall type ${a.tag}${a.name && a.name !== a.tag ? ` (${a.name})` : ""}` : a.name;
    const list = missing.length > 1 ? `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}` : missing[0];
    out.push({
      key: `asm-incomplete:${normTag(a.tag) || a.id}:${missing.length}`,
      severity: missing.some((m) => /board|stud size/.test(m)) ? "high" : "med",
      question: `${name}: the assembly ${a.page ? `on ${sheet(payload, a.page)} ` : ""}doesn't give ${list}. Please confirm.`,
      why: "Needed to price the assembly",
      source: { kind: "drawing", label: `${a.page ? `${sheet(payload, a.page)} · ` : ""}${a.tag || a.name}`, page: s0?.page ?? a.page ?? null, tag: a.tag || "", conditionId: cond?.id || a.conditionId || "" },
      pin: midOf(s0),
    });
  }

  // 3. walls with no tag, per sheet
  const untagged = new Map();
  for (const s of walls) {
    if (s.meta?.source !== "trace" || s.meta?.wallTag) continue;
    if (!untagged.has(s.page)) untagged.set(s.page, []);
    untagged.get(s.page).push(s);
  }
  for (const [page, list] of untagged) {
    if (list.length < 3) continue;
    const lf = list.reduce((n, s) => n + (Number(s.meta?.lf) || 0), 0);
    out.push({
      key: `untagged:${page}`,
      severity: "med",
      question: `${list.length} partitions on ${sheet(payload, page)} have no wall-type tag${lf ? ` (≈${Math.round(lf)} LF)` : ""}. Please confirm their wall type.`,
      why: "Untagged walls are priced as an assumed type",
      source: { kind: "drawing", label: sheet(payload, page), page },
      pin: midOf(list[0]),
    });
  }

  // 4. tag doesn't match the wall drawn
  const conflicts = new Map();
  for (const s of walls) {
    if (!s.meta?.tagCheck) continue;
    const k = `${s.page}|${normTag(s.meta.wallTag)}`;
    if (!conflicts.has(k)) conflicts.set(k, []);
    conflicts.get(k).push(s);
  }
  for (const [k, list] of conflicts) {
    const [page, tag] = k.split("|");
    const why = [].concat(list[0].meta.tagCheck || []).filter(Boolean)[0];
    out.push({
      key: `tagcheck:${k}`,
      severity: "med",
      question: `On ${sheet(payload, Number(page))}, ${list.length} wall${list.length === 1 ? " is" : "s are"} tagged ${tag || "(no tag)"} but drawn ${typeof why === "string" ? why.replace(/^./, (c) => c.toLowerCase()) : "differently from that type"}. Please confirm the wall type.`,
      why: "The tag and the drawing disagree",
      source: { kind: "drawing", label: `${sheet(payload, Number(page))} · ${tag}`, page: Number(page), tag },
      pin: midOf(list[0]),
    });
  }

  // 5. openings to confirm
  const doors = new Map();
  for (const s of live) {
    if (s.kind !== "marker" || !s.meta?.needsCheck) continue;
    if (!doors.has(s.page)) doors.set(s.page, []);
    doors.get(s.page).push(s);
  }
  for (const [page, list] of doors)
    out.push({
      key: `openings:${page}`,
      severity: "low",
      question: `${list.length} opening${list.length === 1 ? "" : "s"} on ${sheet(payload, page)} ${list.length === 1 ? "has" : "have"} no door swing drawn. Please confirm whether ${list.length === 1 ? "it is a door, a cased opening or" : "they are doors, cased openings or"} an open wall.`,
      why: "Doors change the framing and board",
      source: { kind: "drawing", label: sheet(payload, page), page },
      pin: midOf(list[0]),
    });

  // 6. assemblies with quantity but no height anywhere
  for (const c of conds) {
    if (asms.some((a) => a.conditionId === c.id || a.id === c.assemblyId)) continue; // covered above
    const has = shapes.some((s) => s.conditionId === c.id);
    if (!has) continue;
    if (c.type === "linear" && !(Number(c.height) > 0) && !shapes.some((s) => s.conditionId === c.id && Number(s.meta?.height) > 0))
      out.push({
        key: `height:${c.id}`,
        severity: "med",
        question: `${c.name}: no wall height is given. Please confirm whether these walls go to the underside of the deck or to above the ceiling, and the height.`,
        why: "Board and studs are priced by height",
        source: { kind: "drawing", label: c.name, conditionId: c.id, page: shapes.find((s) => s.conditionId === c.id)?.page ?? null },
        pin: midOf(shapes.find((s) => s.conditionId === c.id)),
      });
  }
  const rank = { high: 0, med: 1, low: 2 };
  return out.sort((a, b) => rank[a.severity] - rank[b.severity]);
}
